import React from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { createGlobalStyle } from 'styled-components';
import CssBaseline from '@mui/material/CssBaseline';
import FeatureFlagsContext from './FeatureFlagsContext';
import theme from '../shared/theme';
import PageLayout from './components/PageLayout';
import AccountType from './components/AccountType';
import PracticeInfo from './components/PracticeInfo';
import ClinicPersonnel from './components/ClinicPersonnel';
import ResellerFinish from './components/ResellerFinish';
import FormExpired from './components/FormExpired';
import { updateForm, submitForm } from 'utils/api/vet_reseller';

const GlobalStyle = createGlobalStyle`
  hr {
    border-color: #dfe3e6;
    max-width: none;
    margin: 0;
    margin-top: 0;
  }
  .global-top-nav {
    margin: 0;
  }
  .main-content-container {
    padding-top: 1rem;
    padding-bottom: 5rem;
  }
`;

export default function (props) {
  if (props.expired) {
    return <FormExpired />;
  }

  const featureFlags = {
    showResellerSignatureLink: props.flagShowResellerSignatureLink,
  };

  const [formData, setFormData] = React.useState({
    accountType: props.form.data.accountType,
    practiceInfo: props.form.data.practiceInfo,
    clinicPersonnel: props.form.data.clinicPersonnel,
  });

  const createSaveHandler = (section) => {
    // Returns promise that resolves to the updated form data for each section.
    // Sets validation errors at top level in order handle the situation where
    // a change in one part of the form causes a change in the validation errors
    // for another part of the form.

    return (attrs) => {
      // Set form data in form section's parent component (ie this component) in the event the user navigates back and forth b/w sections
      // What's been happening is that the parent component provides the same data that was present on page load
      // Therefore we can store and update the state the parent component so that it's available when the user navigates back to the section
      setFormData({
        ...formData,
        [section]: { ...formData[section], ...attrs },
      });

      return new Promise((resolve) => {
        return updateForm({ token: props.token, [section]: attrs }).then(
          ({ validationErrors }) => {
            resolve({ validationErrors: validationErrors[section] || {} });
          }
        );
      });
    };
  };

  // Clear the editingPerson errors on page load
  const { editingPerson, ...clinicPersonnelErrorsOnPageLoad } =
    props.form.validationErrors.clinicPersonnel || {};

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <CssBaseline />
      <FeatureFlagsContext.Provider value={featureFlags}>
        <BrowserRouter basename="vet-portal/reseller/forms">
          <Switch>
            <Route
              exact
              path="/contact_info"
              render={({ location }) => (
                <Redirect
                  to={{
                    pathname: '/account_type',
                    search: location.search,
                  }}
                />
              )}
            />

            <Route
              exact
              path="/account_type"
              children={({ location, history }) => (
                <>
                  {props.form.formIsComplete && (
                    <Redirect
                      to={{
                        pathname: '/finish',
                        search: location.search,
                      }}
                    />
                  )}

                  <PageLayout currentSection={location.pathname.slice(1)}>
                    <AccountType
                      onSave={createSaveHandler('accountType')}
                      initialFormState={formData.accountType}
                      initialValidationErrors={
                        props.form.validationErrors.accountType || {}
                      }
                      location={location}
                      goToNextPage={() =>
                        history.push(`/practice_info${location.search}`)
                      }
                      corporationList={props.parentCorporationNameList}
                    />
                  </PageLayout>
                </>
              )}
            />

            <Route
              exact
              path="/practice_info"
              children={({ location, history }) => (
                <>
                  {props.form.formIsComplete && (
                    <Redirect
                      to={{
                        pathname: '/finish',
                        search: location.search,
                      }}
                    />
                  )}

                  <PageLayout currentSection={location.pathname.slice(1)}>
                    <PracticeInfo
                      onSave={createSaveHandler('practiceInfo')}
                      organization={props.organization}
                      initialFormState={formData.practiceInfo}
                      initialValidationErrors={
                        props.form.validationErrors.practiceInfo || {}
                      }
                      location={location}
                      goToPreviousPage={() =>
                        history.push(`/account_type${location.search}`)
                      }
                      goToNextPage={() =>
                        history.push(`/clinic_personnel${location.search}`)
                      }
                    />
                  </PageLayout>
                </>
              )}
            />
            <Route
              exact
              path="/clinic_personnel"
              children={({ location, history }) => (
                <>
                  {props.form.formIsComplete && (
                    <Redirect
                      to={{
                        pathname: '/finish',
                        search: location.search,
                      }}
                    />
                  )}
                  <PageLayout currentSection={location.pathname.slice(1)}>
                    <ClinicPersonnel
                      onSave={createSaveHandler('clinicPersonnel')}
                      initialFormState={props.form.data.clinicPersonnel}
                      initialValidationErrors={clinicPersonnelErrorsOnPageLoad}
                      persistentErrorMessages={props.persistentErrorMessages}
                      location={location}
                      goToPreviousPage={() =>
                        history.push(`/practice_info${location.search}`)
                      }
                      onFinalFormSubmission={() =>
                        submitForm({ token: props.token }).then(() =>
                          history.push(`/finish${location.search}`, {
                            country:
                              props.form.data.practiceInfo.billingCountry,
                          })
                        )
                      }
                    />
                  </PageLayout>
                </>
              )}
            />
            <Route
              exact
              path="/finish"
              children={({ location }) => (
                <ResellerFinish
                  signUrl={props.form.signUrl}
                  location={location}
                  country={props.form.data.practiceInfo.billingCountry}
                  platinumEcomBaseUrl={props.platinumEcomBaseUrl}
                  platinumCanadaEcomBaseUrl={props.platinumCanadaEcomBaseUrl}
                />
              )}
            />
          </Switch>
        </BrowserRouter>
      </FeatureFlagsContext.Provider>
    </ThemeProvider>
  );
}
